import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HelpCircle, Crown, Check, Lock, Sparkles, Store, X, Gift, Unlock, ChevronRight, Flame } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const KIND = {
  reward: { label: "Reward", cls: "text-[#00E676] border-[#00E676]/30 bg-[#00E676]/10", Icon: Gift },
  access: { label: "Accesso", cls: "text-[#7DF9FF] border-[#7DF9FF]/30 bg-[#7DF9FF]/10", Icon: Unlock },
  status: { label: "Status", cls: "text-[#FFD700] border-[#FFD700]/30 bg-[#FFD700]/10", Icon: Crown },
};

const privsOf = (l) =>
  l && l.privileges && l.privileges.length
    ? l.privileges
    : ((l && l.perks) || []).map((p) => ({ label: p, kind: "reward" }));

const territoryLabel = (label, territory) =>
  territory ? label.replace("della tua zona", `di ${territory}`) : label;

export default function RewardsLevels() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [detail, setDetail] = useState(null); // {level, index}
  const [streak, setStreak] = useState(null);

  const loadStreak = () => api.get("/streak/me").then((r) => setStreak(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/rewards/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/growth/summary").then((r) => setSummary(r.data || null)).catch(() => {});
    loadStreak();
  }, []);

  const claimStreak = async () => {
    try {
      const { data: res } = await api.post("/streak/claim");
      const total = (res.granted || []).reduce((a, g) => a + g.xp, 0);
      if (total > 0) {
        toast.success(`🔥 +${total} XP per la tua costanza!`);
        api.get("/rewards/me").then((r) => setData(r.data)).catch(() => {});
      } else {
        toast("Nessun nuovo bonus da riscattare per ora");
      }
      loadStreak();
    } catch { toast.error("Errore nel riscatto"); }
  };

  if (!data) return <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center">Caricamento…</div>;
  const { level, levels, xp, xp_actions, territory } = data;
  const cur = level.current || {};
  const nxt = level.next;
  const topKeys = ["ambassador", "legend", "excellence", "top_partner", "top_org", "expert"];
  const suggested = (xp_actions || []).slice(0, 3);
  const scout = summary?.scout || null;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-16 animate-in fade-in duration-300" data-testid="rewards-page">
      <header className="sticky top-0 z-10 bg-[#0A0A0C]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <button data-testid="rewards-back" onClick={() => navigate(-1)} className="text-white/80"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-['Unbounded'] font-bold text-lg">Livelli & Rewards</h1>
        <button data-testid="rewards-legend-open" onClick={() => setLegendOpen(true)} className="flex items-center gap-1 text-[#00E676] text-xs"><HelpCircle className="h-4 w-4" /> Come funziona?</button>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-6">
        {data.xp_campaign?.active && (
          <div className="rounded-2xl px-4 py-3 bg-gradient-to-r from-[#FF9F45]/20 to-[#FFD700]/10 border border-[#FF9F45]/40 flex items-center gap-2 text-sm font-semibold text-[#FF9F45]" data-testid="rewards-campaign-banner">
            🔥 {data.xp_campaign.label || "Campagna XP"} attiva — XP x{data.xp_campaign.multiplier || 2}
          </div>
        )}

        {/* Progressione attuale */}
        <section className="rounded-3xl p-5 bg-gradient-to-br from-[#00E676]/12 to-[#131316] border border-[#00E676]/25" data-testid="rewards-current">
          <div className="flex items-center gap-2 text-[#00E676] text-xs font-bold uppercase tracking-wide">
            {topKeys.includes(cur.key) ? <Crown className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />} Livello attuale
          </div>
          <div className="mt-1 font-['Unbounded'] font-black text-3xl" data-testid="rewards-level-name">{cur.name}</div>
          <div className="mt-3 text-sm text-white/70">
            {nxt ? <><span className="text-white font-semibold">{xp.toLocaleString()}</span> / {nxt.xp.toLocaleString()} XP</>
                 : <><span className="text-white font-semibold">{xp.toLocaleString()}</span> XP · livello massimo 👑</>}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#00E676] to-[#7DF9FF] rounded-full transition-all duration-500" style={{ width: `${level.progress_pct}%` }} data-testid="rewards-progress" />
            </div>
            <span className="text-xs font-bold text-white/70 w-10 text-right">{level.progress_pct}%</span>
          </div>
          {nxt && <div className="mt-2 text-sm text-[#7DF9FF]">Ti mancano <span className="font-bold">{level.xp_to_next.toLocaleString()} XP</span> per diventare {nxt.name}</div>}

          {nxt && suggested.length > 0 && (
            <div className="mt-4" data-testid="rewards-suggested">
              <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Come arrivarci</div>
              <div className="space-y-1.5">
                {suggested.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl bg-black/30 border border-white/8 px-3 py-2">
                    <span className="text-sm font-bold text-[#00E676] w-16 shrink-0">+{a.xp} XP</span>
                    <span className="text-sm text-white/80">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Riconoscimento Ambassador territoriale */}
        {data.ambassador?.is_ambassador && (
          <section className="rounded-3xl p-5 bg-gradient-to-br from-[#FFD700]/18 to-[#131316] border border-[#FFD700]/40" data-testid="rewards-ambassador">
            <div className="flex items-center gap-2 text-[#FFD700] font-semibold"><Crown className="h-5 w-5" /> Ambassador Fuma{data.ambassador.zone ? ` di ${data.ambassador.zone}` : ""}</div>
            <p className="mt-2 text-sm text-white/75">Sei un punto di riferimento della community sul territorio. Grazie per far crescere Fuma nella tua zona 👑</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.ambassador.is_zone_leader && (
                <span data-testid="ambassador-leader-badge" className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-[#FFD700] text-black"><Crown className="h-3 w-3" /> #1 della tua zona</span>
              )}
              {data.ambassador.zone_rank && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border border-[#FFD700]/40 text-[#FFD700] bg-[#FFD700]/10">Posizione zona: #{data.ambassador.zone_rank}</span>
              )}
            </div>
          </section>
        )}

        {/* Streak settimanale — premia la costanza, senza pressione */}
        {streak && (streak.streak_weeks > 0 || (streak.milestones || []).length > 0) && (
          <section className="rounded-3xl p-5 bg-gradient-to-br from-[#FF6B35]/14 to-[#131316] border border-[#FF6B35]/30" data-testid="rewards-streak">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[#FF6B35] font-semibold"><Flame className="h-5 w-5" /> Costanza settimanale</div>
              <div className="text-right"><span data-testid="streak-weeks" className="font-['Unbounded'] font-black text-2xl text-[#FF6B35]">{streak.streak_weeks}</span><span className="text-xs text-white/50 ml-1">sett.</span></div>
            </div>
            <p className="mt-1 text-sm text-white/60">Resta attivo ogni settimana (un ordine, un timbro o una missione) e sblocca bonus XP. Nessuna fretta: la settimana in corso non rompe la serie.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(streak.milestones || []).map((m) => (
                <span key={m.weeks} data-testid={`streak-milestone-${m.weeks}`}
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${m.claimed ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/10" : m.reached ? "border-[#FF6B35]/50 text-[#FF6B35] bg-[#FF6B35]/10" : "border-white/10 text-white/40 bg-white/5"}`}>
                  {m.claimed ? <Check className="h-3 w-3" /> : m.reached ? <Flame className="h-3 w-3" /> : <Lock className="h-2.5 w-2.5" />}
                  {m.weeks} sett · +{m.xp} XP
                </span>
              ))}
            </div>
            {(streak.milestones || []).some((m) => m.claimable) && (
              <button data-testid="streak-claim" onClick={claimStreak} className="mt-3 w-full bg-[#FF6B35] text-black font-semibold rounded-xl py-2.5 active:scale-[0.99] transition-transform">Riscatta bonus costanza</button>
            )}
            {streak.next && !(streak.milestones || []).some((m) => m.claimable) && (
              <div className="mt-3 text-xs text-white/50">Prossimo traguardo: <span className="text-[#FF6B35] font-semibold">{streak.next.weeks} settimane</span> → +{streak.next.xp} XP</div>
            )}
          </section>
        )}

        {/* Percorso referral attività */}
        {scout && (
          <section className="rounded-3xl p-5 bg-[#131316] border border-[#FFD700]/25" data-testid="rewards-scout">
            <div className="flex items-center gap-2 text-[#FFD700] font-semibold"><Store className="h-4 w-4" /> Porta Fuma nel tuo quartiere</div>
            {(() => {
              const portate = summary?.businesses_entered ?? 0;
              const vb = scout.valid_businesses || 0;
              const target = scout.next?.threshold || scout.current?.threshold || 10;
              const pct = Math.min(100, Math.round((vb / target) * 100));
              const reward = scout.next?.reward_points || 0;
              return (<>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-black/30 border border-white/8 px-3 py-2"><div className="text-xl font-black">{portate}</div><div className="text-[11px] text-white/50">attività portate</div></div>
                  <div className="rounded-xl bg-black/30 border border-[#00E676]/20 px-3 py-2"><div className="text-xl font-black text-[#00E676]">{vb}</div><div className="text-[11px] text-white/50">attivate ✅</div></div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
                  <span>0</span>
                  <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#FFD700] to-[#00E676] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
                  <span>{target}</span>
                </div>
                {scout.next && (
                  <div className="mt-2 text-xs text-[#FFD700]">
                    Prossimo traguardo: <span className="font-semibold">{scout.next.name}</span> — ancora {Math.max(0, target - vb)} attivate{reward ? ` · reward ${reward.toLocaleString()} punti` : ""}.
                  </div>
                )}
                <div className="mt-1 text-[11px] text-white/40">La ricompensa importante arriva solo quando l'attività viene realmente attivata, non alla semplice segnalazione.</div>
              </>);
            })()}
          </section>
        )}

        {/* Scala livelli con privilegi */}
        <section data-testid="rewards-ladder">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold">La tua scala</h2>
            <div className="text-[10px] text-white/40 flex items-center gap-2">
              <span className="text-[#00E676]">● Reward</span><span className="text-[#7DF9FF]">● Accesso</span><span className="text-[#FFD700]">● Status</span>
            </div>
          </div>
          <div className="space-y-2">
            {levels.map((l, i) => {
              const reached = i <= level.index;
              const isCur = i === level.index;
              const privs = privsOf(l).slice(0, 3);
              return (
                <button key={l.key} data-testid={`rewards-level-${l.key}`} onClick={() => setDetail({ level: l, index: i })}
                  className={`w-full text-left rounded-2xl px-4 py-3 border transition-colors ${isCur ? "border-[#00E676]/50 bg-[#00E676]/10" : reached ? "border-white/10 bg-[#131316]" : "border-white/5 bg-[#0F0F12]"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${reached ? "bg-[#00E676] text-black" : "bg-white/10 text-white/50"}`}>{reached ? <Check className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />}</div>
                    <div className="flex-1 min-w-0"><div className="font-semibold text-sm flex items-center gap-1.5">{l.name}{l.key === "ambassador" && <Crown className="h-3.5 w-3.5 text-[#FFD700]" />}</div><div className="text-xs text-white/50">{l.xp.toLocaleString()} XP</div></div>
                    {isCur && <span className="text-[10px] font-bold text-[#00E676] uppercase shrink-0">Sei qui</span>}
                    <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />
                  </div>
                  <div className={`mt-2 flex flex-wrap gap-1.5 ${reached ? "" : "opacity-70"}`}>
                    {privs.map((p, j) => {
                      const k = KIND[p.kind] || KIND.reward;
                      return (
                        <span key={j} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${reached ? k.cls : "text-white/40 border-white/10 bg-white/5"}`}>
                          {reached ? <k.Icon className="h-3 w-3" /> : <Lock className="h-2.5 w-2.5" />}
                          {territoryLabel(p.label, territory)}
                        </span>
                      );
                    })}
                    {privsOf(l).length > 3 && <span className="text-[11px] text-white/40 px-1">+{privsOf(l).length - 3}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </main>

      {/* Modale dettaglio livello */}
      {detail && (() => {
        const l = detail.level;
        const reached = detail.index <= level.index;
        const missing = Math.max(0, (l.xp || 0) - xp);
        const privs = privsOf(l);
        return (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={() => setDetail(null)} data-testid="rewards-level-detail">
            <div className="bg-[#131316] border border-white/12 rounded-3xl p-5 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="font-['Unbounded'] font-bold text-xl flex items-center gap-2">{l.name}{l.key === "ambassador" && <Crown className="h-5 w-5 text-[#FFD700]" />}</h3>
                <button data-testid="rewards-detail-close" onClick={() => setDetail(null)}><X className="h-5 w-5 text-white/60" /></button>
              </div>
              <div className={`mt-2 inline-flex items-center gap-2 text-xs font-bold px-3 py-1 rounded-full ${reached ? "bg-[#00E676]/15 text-[#00E676]" : "bg-white/10 text-white/60"}`}>
                {reached ? <><Check className="h-3.5 w-3.5" /> Sbloccato</> : <><Lock className="h-3 w-3" /> Ti mancano {missing.toLocaleString()} XP</>}
              </div>
              <div className="mt-1 text-xs text-white/50">Soglia: {(l.xp || 0).toLocaleString()} XP</div>

              <div className="mt-4 space-y-2">
                <div className="text-xs uppercase tracking-wide text-white/40">Tutti i privilegi</div>
                {privs.map((p, j) => {
                  const k = KIND[p.kind] || KIND.reward;
                  return (
                    <div key={j} className={`flex items-center gap-3 rounded-xl px-3 py-2 border ${reached ? "border-white/10 bg-black/30" : "border-white/5 bg-white/5 opacity-70"}`}>
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 border ${reached ? k.cls : "text-white/40 border-white/10"}`}>{reached ? <k.Icon className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />}</div>
                      <span className="text-sm text-white/85 flex-1">{territoryLabel(p.label, territory)}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${k.cls}`}>{k.label}</span>
                    </div>
                  );
                })}
              </div>

              {l.reward && (
                <div className="mt-4 rounded-xl border border-[#00E676]/25 bg-[#00E676]/10 px-3 py-2 text-sm text-[#00E676]">
                  <span className="font-semibold">{reached ? "Reward ottenuto: " : "Reward al raggiungimento: "}</span>{l.reward}
                </div>
              )}
              {l.credits > 0 && l.credits_active && (
                <div className="mt-2 text-xs text-white/60">💰 {l.credits.toLocaleString()} crediti {reached ? "assegnati" : "al raggiungimento"}</div>
              )}
            </div>
          </div>
        );
      })()}

      {legendOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={() => setLegendOpen(false)} data-testid="rewards-legend">
          <div className="bg-[#131316] border border-white/12 rounded-3xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="font-['Unbounded'] font-bold text-lg">Come funzionano livelli e Rewards?</h3><button data-testid="rewards-legend-close" onClick={() => setLegendOpen(false)}><X className="h-5 w-5 text-white/60" /></button></div>
            <div className="mt-3 rounded-xl bg-black/30 border border-white/8 p-3 text-xs text-white/70 space-y-1">
              <div><span className="text-[#00E676] font-semibold">Reward</span> → ottieni qualcosa</div>
              <div><span className="text-[#7DF9FF] font-semibold">Accesso</span> → puoi fare qualcosa prima o in esclusiva</div>
              <div><span className="text-[#FFD700] font-semibold">Status</span> → vieni riconosciuto nella community</div>
            </div>
            <div className="mt-3 space-y-2.5">
              {(data.legend || []).map((it, i) => (<div key={i} className="flex gap-3"><div className="text-[#00E676] font-bold text-sm w-16 shrink-0">{it.term}</div><div className="text-sm text-white/70">{it.desc}</div></div>))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
